import { createContext, useContext, useState, useEffect } from "react";
import { getAuthService, getFirestoreService } from "../lib/firebaseClient";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let userDocUnsub = null;
    let unsubscribe = () => {};

    // Defer Firebase auth setup until idle to avoid blocking LCP
    const defer = (cb) => ("requestIdleCallback" in window ? requestIdleCallback(cb) : setTimeout(cb, 1200));

    defer(async () => {
      const auth = await getAuthService();
      const { onAuthStateChanged } = await import("firebase/auth");

      unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (user) {
          try {
            // Force refresh to pick up latest custom claims
            const tokenResult = await user.getIdTokenResult(true);
            let role = tokenResult.claims?.premium ? "premium" : "user";

          // Load Firestore only when needed (user logged in)
          const db = await getFirestoreService();
          const { doc, getDoc, onSnapshot } = await import("firebase/firestore");
          const docRef = doc(db, "users", user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists() && docSnap.data().role) {
            role = String(docSnap.data().role).toLowerCase();
          }

          user.role = role;
          setCurrentUser(user);

          // Live updates from Firestore
          userDocUnsub = onSnapshot(docRef, (snap) => {
            if (snap.exists() && snap.data().role) {
              const newRole = String(snap.data().role).toLowerCase();
              if (user.role !== newRole) {
                user.role = newRole;
                setCurrentUser({ ...user });
              }
            }
          });
        } catch (err) {
          console.error("Erreur AuthContext:", err);
          setCurrentUser(null);
        }
      } else {
        if (userDocUnsub) {
          userDocUnsub();
          userDocUnsub = null;
        }
        setCurrentUser(null);
      }
      setLoading(false);
      });
    });

    return () => {
      if (userDocUnsub) userDocUnsub();
      unsubscribe && unsubscribe();
    };
  }, []);

  const value = {
    currentUser,
    loading, // 👈 exposé pour PremiumContent
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
